import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessageInputProps {
  onSend: (content: string) => void;
  onTyping: (isTyping: boolean) => void;
  onAiSuggest?: () => void;
  isSending?: boolean;
  isAiLoading?: boolean;
  placeholder?: string;
}

export function MessageInput({
  onSend,
  onTyping,
  onAiSuggest,
  isSending,
  isAiLoading,
  placeholder = 'Type a message...',
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);

    // Handle typing indicator
    onTyping(true);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      onTyping(false);
    }, 2000);
  };

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed || isSending) return;

    onSend(trimmed);
    setMessage('');
    onTyping(false);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-brand-border bg-brand-surface p-3">
      <div className="flex items-end gap-2">
        {onAiSuggest && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onAiSuggest}
            disabled={isAiLoading}
            className="shrink-0 text-brand-primary hover:text-brand-primary/80"
            title="Get AI suggestions"
          >
            {isAiLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
          </Button>
        )}

        <Textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            'flex-1 min-h-[40px] max-h-[120px] resize-none',
            'rounded-2xl border-brand-border bg-brand-background text-white placeholder:text-brand-muted',
            'focus:border-brand-primary focus:ring-brand-primary/20',
            'py-2 px-4'
          )}
          rows={1}
        />

        <Button
          type="button"
          onClick={handleSend}
          disabled={!message.trim() || isSending}
          className="shrink-0 rounded-full w-10 h-10 p-0 bg-brand-primary hover:bg-brand-primary/90"
        >
          {isSending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </Button>
      </div>
    </div>
  );
}
